import React, { useEffect, useRef, useState } from "react";
import { Button, Table, TableColumnsType } from "antd";
import FAQDrawer from "./FAQDrawer";
import FAQFilter from "./FAQFilter";
import { FaqType } from "../../../../Interface";
import { faqData } from "../../../../Utils/faqData";
import { generateFaqLink } from "../../../../Utils/generic-functions";
import useOrganization from "../../../../Hooks/useOrganization";
import "./faq.scss";

const FAQ: React.FC = () => {
  const { org } = useOrganization();
  const themeColor = org?.theme?.primaryColor || "#1890ff";

  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [selectedFaqLink, setSelectedFaqLink] = useState<string>("");
  const [filteredFaqData, setFilteredFaqData] = useState<FaqType[]>(faqData);
  const [selectedFileExtension, setSelectedFileExtension] =
    useState<string>("html");
  const [tableScrollHeight, setTableScrollHeight] = useState(400);
  const tableRef = useRef<HTMLDivElement>(null);

  const handleViewSelectedFaq = (faq: FaqType) => {
    const faqLink = generateFaqLink(faq.title, faq.fileExtension);
    setSelectedFaqLink(faqLink);
    setSelectedFileExtension(faq.fileExtension || "html");
    setIsDrawerVisible(true);
  };

  const handleSearch = (searchText: string) => {
    if (!searchText) {
      setFilteredFaqData(faqData);
      return;
    }

    const lowerSearch = searchText.toLowerCase();

    const filtered = faqData.filter(
      (faq) =>
        faq.title?.toLowerCase().includes(lowerSearch) ||
        faq.description?.toLowerCase().includes(lowerSearch)
    );

    setFilteredFaqData(filtered);
  };

  const columns: TableColumnsType<FaqType> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 400,
      render: (text: string) => <div className="faq-table-cell">{text}</div>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 600,
      render: (text: string) => <div className="faq-table-cell">{text}</div>,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      render: (_, record) => {
        return (
          <Button
            type="link"
            size="small"
            onClick={() => handleViewSelectedFaq(record)}
            style={{
              color: themeColor,
              padding: "4px 8px",
              height: "auto",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            View Details
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    const updateTableHeight = () => {
      if (tableRef.current) {
        const computedStyle = getComputedStyle(tableRef.current);
        const heightValue = computedStyle.getPropertyValue(
          "--table-scroll-height"
        );
        if (heightValue) {
          const heightMatch = heightValue.match(
            /calc\([^)]*100\s*-\s*(\d+)px\)/
          );
          if (heightMatch) {
            const vh = window.innerHeight;
            const offset = parseInt(heightMatch[1]);
            const calculatedHeight = vh - offset;
            setTableScrollHeight(Math.max(calculatedHeight, 200));
          }
        }
      }
    };

    updateTableHeight();

    window.addEventListener("resize", updateTableHeight);

    return () => {
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  return (
    <section className="faq-tab-container">
      <FAQFilter onSearch={handleSearch} />
      <div className="faq-list-table" ref={tableRef}>
        <Table
          columns={columns}
          dataSource={filteredFaqData}
          rowKey="title"
          size="small"
          pagination={false}
          scroll={{
            y: tableScrollHeight,
            x: "max-content",
          }}
        />
      </div>
      <FAQDrawer
        isVisible={isDrawerVisible}
        onClose={() => setIsDrawerVisible(false)}
        faqLink={selectedFaqLink}
        fileExtension={selectedFileExtension}
      />
    </section>
  );
};

export default FAQ;
